/**
 * Recursively computes the height of the provided parent such that exactly
 * `lineCount` lines are included.
 *
 * NB: Simply using lineHeight / lineCount is not sufficient as this method
 * supports block children that may contain margin, different font-sizes /
 * line-heights and more. As such a recursive implementation has been
 * implemented to achieve this.
 */
function getHeightContainingLines(parent, lineCount) {
  if (!parent) {
    return 0;
  }
  let height = 0;
  let currentLineCount = 0;
  let prevSiblingName = "";
  let prevChildHeight = 0;
  let prevOffsetTop = -1;
  let prevMarginBottom = 0;
  for (const child of Array.from(parent.children)) {
    const childStyles = getComputedStyle(child);
    const childHeight = child.scrollHeight || child.getBoundingClientRect().height;
    const childMarginBottom = parseFloat(childStyles["marginBottom"]);
    const fontSize = parseFloat(childStyles.fontSize);
    const lineHeight = childStyles.lineHeight === "normal" ? fontSize * 1.2 : parseFloat(childStyles.lineHeight);
    let linesInChild = Math.round(childHeight / lineHeight);

    // Line-breaks add slight spacing between lines, make sure we compensate for
    // this
    if (child.tagName === "BR") {
      // In case of multiple line-breaks then all but the first adds a new line
      if (prevSiblingName === "BR") {
        currentLineCount += 1;
        height += lineHeight;
      } else {
        height += 3;
      }
      prevSiblingName = child.tagName;
      continue;
    }

    // We only need to consider children that are blocks as they are the only
    // which will actually contain lines (whereas inline elements consitute to
    // the blocks total lines)
    if (!(child instanceof HTMLElement)) {
      continue;
    }

    // In case the current child pushes us over the desired amount of lines,
    // then focus analysis on this element! ...
    if (linesInChild + currentLineCount >= lineCount) {
      // In case the child which cases overflow contains children, then analyse
      // these to determine if block children exists inside...
      if (child.children.length > 0) {
        height += getHeightContainingLines(child, lineCount - currentLineCount);
      } else {
        // ... else we can simply add the total amount of lines that this
        // element has.
        height += (lineCount - currentLineCount) * lineHeight;
      }
      break;
    }

    // In case we've encountered two inline children in a row that have been on
    // the same line, then simply negate the line that the previous child added
    // (since it'll be re-applied by this child)
    if (prevOffsetTop === child.offsetTop) {
      linesInChild -= 1;
      height -= prevChildHeight;
    }

    // ... otherwise, in case the current child does not constitue an overflow,
    // then simply add its height to the total height required to obtain the
    // desired lineCount
    currentLineCount += linesInChild;
    height += childHeight + Math.max(0, parseFloat(childStyles["marginTop"]) - prevMarginBottom) + childMarginBottom;
    prevOffsetTop = child.offsetTop;
    prevMarginBottom = childMarginBottom;
    prevSiblingName = child.tagName;
    prevChildHeight = childHeight;
  }

  // In case the provided parent didn't have any relevant children, then we can
  // fallback to the regular lineCount / lineHeight trick to determine the
  // required height
  if (height === 0) {
    const style = getComputedStyle(parent);
    height += lineCount * parseFloat(style.lineHeight);
  }
  return height;
}

export { getHeightContainingLines };
//# sourceMappingURL=util.get-height-containing-lines.js.map
